import { RefObject, useEffect, useRef } from 'react';

/**
 * Escape closes the dialog; focus moves into it on open and back to where it was
 * on close. The key is taken in the capture phase and stopped there: a dialog can
 * open on top of other dialogs and pages that also answer Escape (the record
 * card, the course page's "back"), and only the top one may react.
 */
export const useModalDismiss = (dialogRef: RefObject<HTMLElement>, onClose: () => void): void => {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onCloseRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      previous?.focus({ preventScroll: true });
    };
  }, [dialogRef]);
};
